import { Plan, useI18n } from '@springbig/shared';
import { VStack, Box, Image } from 'native-base';
import Button from '~/shared/Button';
import React from 'react';
import { useGeneralStyles, useSubscriptionsStyles } from '~/shared/styles';
import { Caption, H3, H2 } from '~/shared/Typography';
import PlanBenefits from '~/pages/subscriptions/components/PlanBenefits';
import { faGem } from '@fortawesome/free-regular-svg-icons';
import { SpIcon } from '~/shared/Icon';

type PlanDetailsProps = {
    plan: Plan;
    onCancel: () => void;
    onProceed: () => void;
};

const PlanDetails = ({
    plan,
    onCancel,
    onProceed
}: PlanDetailsProps) => {

    const gStyle = useGeneralStyles();
    const { icon } = useSubscriptionsStyles();
    const { t } = useI18n();

    const billing = {
        both: t('subscriptions.billing.both', [plan.recurring_amount.toFixed(2), plan.yearly_recurring_amount.toFixed(2)]),
        monthly: t('subscriptions.billing.monthly', [plan.recurring_amount.toFixed(2)]),
        yearly: t('subscriptions.billing.yearly', [plan.yearly_recurring_amount.toFixed(2)]),
    };

    return (
        <VStack
            justifyContent={'center'}
            textAlign={'center'}
            space={plan.plan_description_type === 'image' ? 4 : 8}
            py={16}
            px={4}
        >
            <H3>{ t('subscriptions.details.title') }</H3>

            <Box alignSelf={'center'}>
                <SpIcon
                    color={icon}
                    icon={faGem}
                    size={36}
                />
            </Box>

            <Caption>
                { t('subscriptions.details.description') }
            </Caption>

            <VStack>
                <H3>{ t('subscriptions.pricing_title') }</H3>
                { plan.trial_period > 0 &&
                    <Caption>{ t('subscriptions.free_trial', [plan.trial_period]) }</Caption>
                }
                <Caption>{ t('subscriptions.pricing', [billing[plan.billing_schedule]]) }</Caption>
            </VStack>

            { plan.plan_description_type === 'image' ?
                <Box
                    w={'100%'}
                    h={'400px'}
                >
                    <Image
                        w={'100%'}
                        h={'100%'}
                        rounded={10}
                        src={`${plan?.plan_description_image?.src}`}
                    />
                </Box>
                :
                <VStack
                    borderStyle={'solid'}
                    borderWidth={1}
                    borderColor={gStyle.button_background}
                    bg={gStyle.section_background}
                    borderRadius={8}
                    p={4}
                    space={4}
                >
                    <H2
                        color={gStyle.button_background}
                        textAlign={'center'}
                        my={0}
                    >
                        { plan.name }
                    </H2>
                    <PlanBenefits plan={plan} alignItems={'flex-start'} />
                </VStack>
            }

            <VStack space={plan.plan_description_type === 'image' ? 4 : 8}>
                <Button
                    bg={gStyle.button_background}
                    _text={{ color: gStyle.button_text }}
                    onPress={onProceed}
                >
                    {
                        plan.trial_period > 0 ?
                            t('subscriptions.details.try_free_button')
                            :
                            t('subscriptions.details.try_button')
                    }
                </Button>

                <Button
                    bg={gStyle.button_neutral_background}
                    _text={{ color: gStyle.button_neutral_text }}
                    onPress={onCancel}
                >
                    { t('subscriptions.back_button') }
                </Button>
            </VStack>
        </VStack>
    );
};

export default PlanDetails;
